import classNames from "classnames";
import { useState } from "react";
import { BaseButton } from "../../../../../components/Button/BaseButton.tsx";
import { BaseImage } from "../../../../../components/Content/BaseImage.tsx";
import { ClickableIcon } from "../../../../../components/Icon/ClickableIcon.tsx";
import { Popover } from "../../../../../components/Popover/Popover.tsx";
import { useSelectedModelsSettingsAndActions } from "../../../../boardState/hooks/useSelectedModelsSettingsAndActions.ts";
import { boardModelPickerStore } from "../../../../boardState/stores/boardModelPickerStore.ts";
import type { ModelGenerationParams } from "../../../../boardState/stores/selectedModels/types.ts";
import {
  CHARACTER_MODEL_TYPE,
  DEFAULT_STYLE_MODELS_PROPERTIES,
  OBJECT_MODEL_TYPE,
  STYLE_MODEL_TYPE,
  UPLOAD_MAX_IMAGES,
} from "../../../../constants.ts";
import type {
  ModelType,
  ModelWithIsCompatibleAndTags,
} from "../../../../types.ts";
import { MODEL_TYPE_LABELS } from "../../../display.ts";
import { useBoardUuid } from "../../../hooks/useBoardUuid.ts";
import { useModelUpload } from "../../../hooks/useModelUpload.ts";
import type {
  ModelActions,
  ModelCompatibleTool,
  ModelsSettings,
} from "../../../types.ts";
import { PanelSection } from "../PanelSection.tsx";
import { AddModelButton } from "./AddModelButton.tsx";
import { ModelSettingsMenu } from "./ModelSettingsMenu.tsx";

export const ModelSelectionSection = ({
  tool,
  modelTypes,
  doesObjectModelSupportUpload = false,
  doesCharacterModelSupportUpload = false,
  doesStyleModelSupportUpload = false,
  extraModelSpecificComponents = {} as Record<ModelType, React.ReactNode>,
}: {
  tool: ModelCompatibleTool;
  modelTypes?: ModelType[];
  doesObjectModelSupportUpload?: boolean;
  doesCharacterModelSupportUpload?: boolean;
  doesStyleModelSupportUpload?: boolean;
  extraModelSpecificComponents?: Partial<Record<ModelType, React.ReactNode>>;
}) => {
  const boardUuid = useBoardUuid();

  const {
    selectedModelsSettings,
    modelActions,
    numberOfOtherCompatibleModels,
  } = useSelectedModelsSettingsAndActions(tool, boardUuid);

  const areThereAnyStyleCompatibleModels =
    numberOfOtherCompatibleModels[STYLE_MODEL_TYPE] > 0;

  const areThereAnyCharacterCompatibleModels =
    numberOfOtherCompatibleModels[CHARACTER_MODEL_TYPE] > 0;

  const areThereAnyObjectCompatibleModels =
    numberOfOtherCompatibleModels[OBJECT_MODEL_TYPE] > 0;

  return (
    <>
      {(modelTypes === undefined ||
        modelTypes.includes(STYLE_MODEL_TYPE) ||
        areThereAnyStyleCompatibleModels) && (
        <ModelSpecificSelectionSection
          tool={tool}
          selectedModelsSettings={selectedModelsSettings}
          modelType={STYLE_MODEL_TYPE}
          modelActions={modelActions}
          addModelElement={
            <AddModelButton
              tool={tool}
              modelType={STYLE_MODEL_TYPE}
              isDisabled={!areThereAnyStyleCompatibleModels}
              allowDirectUpload={doesStyleModelSupportUpload}
            />
          }
          doesModelSupportUpload={false}
        >
          {extraModelSpecificComponents[STYLE_MODEL_TYPE]}
        </ModelSpecificSelectionSection>
      )}
      {(modelTypes === undefined ||
        modelTypes.includes(CHARACTER_MODEL_TYPE) ||
        areThereAnyCharacterCompatibleModels) && (
        <ModelSpecificSelectionSection
          tool={tool}
          selectedModelsSettings={selectedModelsSettings}
          modelType={CHARACTER_MODEL_TYPE}
          modelActions={modelActions}
          addModelElement={
            <AddModelButton
              tool={tool}
              modelType={CHARACTER_MODEL_TYPE}
              isDisabled={!areThereAnyCharacterCompatibleModels}
              allowDirectUpload={doesCharacterModelSupportUpload}
            />
          }
          doesModelSupportUpload={doesCharacterModelSupportUpload}
        >
          {extraModelSpecificComponents[CHARACTER_MODEL_TYPE]}
        </ModelSpecificSelectionSection>
      )}
      {(modelTypes === undefined ||
        modelTypes.includes(OBJECT_MODEL_TYPE) ||
        areThereAnyObjectCompatibleModels) && (
        <ModelSpecificSelectionSection
          tool={tool}
          selectedModelsSettings={selectedModelsSettings}
          modelType={OBJECT_MODEL_TYPE}
          isBeta
          modelActions={modelActions}
          addModelElement={
            <AddModelButton
              tool={tool}
              modelType={OBJECT_MODEL_TYPE}
              isDisabled={!areThereAnyObjectCompatibleModels}
              allowDirectUpload={doesObjectModelSupportUpload}
            />
          }
          doesModelSupportUpload={doesObjectModelSupportUpload}
        >
          {extraModelSpecificComponents[OBJECT_MODEL_TYPE]}
        </ModelSpecificSelectionSection>
      )}
    </>
  );
};

const ModelSpecificSelectionSection = ({
  selectedModelsSettings,
  tool,
  modelType,
  isBeta = false,
  addModelElement,
  modelActions,
  children,
  doesModelSupportUpload,
}: {
  selectedModelsSettings: ModelsSettings;
  tool: ModelCompatibleTool;
  modelType: ModelType;
  isBeta?: boolean;
  addModelElement?: React.ReactNode;

  modelActions: ModelActions;
  children?: React.ReactNode;
  doesModelSupportUpload: boolean;
}) => {
  const modelSpecificSelectedModels = selectedModelsSettings.models.filter(
    (model) => model.type === modelType,
  );

  const generationParams = selectedModelsSettings.generationParams.filter(
    (it) => modelSpecificSelectedModels.some((model) => model.uuid === it.uuid),
  );

  return (
    <PanelSection
      title={MODEL_TYPE_LABELS[modelType]}
      rightItem={addModelElement}
      isBeta={isBeta}
    >
      {modelSpecificSelectedModels.length > 0 &&
        modelSpecificSelectedModels.map((model, index) => (
          <ModelItem
            key={model.uuid}
            model={model}
            generationParams={generationParams[index]}
            onModelClick={() => {
              boardModelPickerStore.setState({
                tool,
                modelType,
                isOpen: true,
                initialSelectedModelUuid: model.uuid,
              });
            }}
            modelActions={modelActions}
            doesModelSupportUpload={doesModelSupportUpload}
          />
        ))}
      {children}
    </PanelSection>
  );
};

const ModelItem = ({
  model,
  generationParams,
  onModelClick,
  modelActions,
  doesModelSupportUpload,
}: {
  model: ModelWithIsCompatibleAndTags;
  generationParams: ModelGenerationParams;
  onModelClick: () => void;
  modelActions: ModelActions;
  doesModelSupportUpload: boolean;
}) => {
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);

  const { openFileBrowser, isLoading, getInputProps } = useModelUpload(
    model.type,
    UPLOAD_MAX_IMAGES,
    (modelUuid, isModelTrained) => {
      modelActions.removeModel(model.uuid);
      modelActions.addModel(modelUuid, isModelTrained);
    },
  );

  return (
    <div className="flex-row w-full gap-100 ">
      <div className="relative group flex-fill h-[120px] p-200 rounded-150 bg-input-surface-rest border-input-border-width border-dashed border-input-border-active overflow-clip">
        <BaseImage
          src={
            model.thumbnail_url === ""
              ? DEFAULT_STYLE_MODELS_PROPERTIES[model.uuid]?.thumbnail_url
              : model.thumbnail_url
          }
          className="w-full h-full"
          imageClassName="w-full h-full object-cover"
        />
        <div className="absolute left-0 bottom-0 rounded-b-100 w-full h-full bg-gradient-to-t from-[#494949]/80 to-80%" />
        <div className="absolute bottom-200 w-[90%] truncate body-sm-default text-white visible group-hover:invisible">
          {model.name ?? "Untitled"}
        </div>
        <div className="absolute left-0 bottom-0 w-full h-full invisible group-hover:visible bg-button-surface-error-disabled" />
        <div className="absolute left-0 top-0 w-full h-full flex-col-center gap-200 invisible group-hover:visible">
          <BaseButton
            onClick={onModelClick}
            className="w-[122px] label-md-semibold text-center rounded-100 bg-surface-primary-rest border p-100 hover:brightness-95 active:brightness-90"
          >
            Select from library
          </BaseButton>
          {doesModelSupportUpload && (
            <BaseButton
              className="w-[122px] label-md-semibold text-center rounded-100 bg-surface-primary-rest border p-100 hover:brightness-95 active:brightness-90"
              onClick={openFileBrowser}
              loading={isLoading}
            >
              <input {...getInputProps()} />
              Upload up to {UPLOAD_MAX_IMAGES} images
            </BaseButton>
          )}
        </div>
      </div>
      <Popover
        side="right"
        align="start"
        sideOffset={4}
        isOpen={isSettingsMenuOpen}
        onOpenChange={(open) => {
          setIsSettingsMenuOpen(open);
        }}
        content={
          generationParams.model_generation_type !== undefined ? (
            <ModelSettingsMenu
              model={model}
              generationParams={generationParams}
              modelActions={modelActions}
            />
          ) : null
        }
      >
        <ClickableIcon
          name={
            generationParams.model_generation_type !== undefined
              ? "Settings2"
              : "TriangleAlert"
          }
          className={classNames(
            isSettingsMenuOpen && "bg-button-surface-tertiary-active",
          )}
          variant="tertiary"
          size="sm"
          disabled={generationParams.model_generation_type === undefined}
        />
      </Popover>
      <ClickableIcon
        onClick={() => {
          modelActions.removeModel(model.uuid);
        }}
        name="Minus"
        variant="tertiary"
        size="sm"
      />
    </div>
  );
};
